import { ArrowRight, ChevronRight, Shield, Zap, Globe } from 'lucide-react';
import { Button, buttonVariants } from '@/components/ui/button';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { cn } from "@/lib/utils";

const Hero = () => {
  return (
    <div className="min-h-screen flex items-center py-20 px-4 sm:px-6 relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-primary/5 via-background to-background -z-10" />
      <div className="absolute right-0 top-1/3 -z-10">
        <div className="h-[30rem] w-[30rem] rounded-full bg-primary/5 blur-3xl opacity-70"></div>
      </div>
      <div className="absolute left-0 bottom-1/3 -z-10">
        <div className="h-[30rem] w-[30rem] rounded-full bg-primary/5 blur-3xl opacity-70"></div>
      </div>
      <div className="absolute inset-0 bg-grid-pattern opacity-10 -z-10"></div>

      <div className="max-w-7xl mx-auto w-full">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
          >
            <div className="inline-flex items-center px-3 py-1 text-sm rounded-full bg-primary/10 text-primary mb-6">
              <span className="font-medium">Reimagining Possibilities</span>
            </div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-primary to-blue-500 leading-tight">
              Real solutions for building applications on the Blockchain
            </h1>
            <motion.p
              className="text-xl text-muted-foreground mb-8 leading-relaxed"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              Enable a more trustworthy, value-centric web
            </motion.p>

            <motion.div
              className="grid grid-cols-1 sm:grid-cols-2 gap-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
            >
              <a
                href="https://docs.projectbabbage.com/docs/quickstarts/getting-a-wallet"
                target="_blank"
                rel="noopener noreferrer"
                className={cn(buttonVariants({ size: "lg" }), "group")}
              >
                Use Metanet Apps
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </a>
              
              <a
                href="https://docs.projectbabbage.com/docs/quickstarts/your-first-bsv-app"
                target="_blank"
                rel="noopener noreferrer"
                className={cn(buttonVariants({ variant: "outline", size: "lg" }), "group")}
              >
                Build Metanet Apps
                <ChevronRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </a>

              <Link
                to="/contact"
                className={cn(buttonVariants({ variant: "secondary", size: "lg" }), "group")}
              >
                Contact Sales
                <ChevronRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </motion.div>

            {/* <motion.div
              className="mt-12"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.7 }}
            >
              <p className="text-sm text-muted-foreground mb-4">TRUSTED BY COMPANIES WORLDWIDE</p>

              <div className="flex gap-6 items-center">
                <div className="flex -space-x-2">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="h-8 w-8 rounded-full bg-muted ring-2 ring-background" />
                  ))}
                </div>
                <div className="text-sm text-muted-foreground flex items-center">
                  <span className="text-foreground font-medium">500+</span>
                  <span className="ml-1">happy clients</span>
                </div>
              </div>
            </motion.div> */}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            {/* Dashboard preview */}
            <div className="relative">
              <div className="absolute -inset-1 bg-gradient-to-r from-primary/20 to-blue-500/20 rounded-2xl blur-md"></div>
              <div className="relative bg-card border rounded-xl overflow-hidden shadow-2xl">
                {/* Header */}
                <div className="bg-background/90 border-b px-4 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="flex gap-1">
                      <div className="h-3 w-3 rounded-full bg-red-500"></div>
                      <div className="h-3 w-3 rounded-full bg-yellow-500"></div>
                      <div className="h-3 w-3 rounded-full bg-green-500"></div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="h-2 w-24 bg-muted rounded-full"></div>
                    <div className="h-6 w-6 rounded-full bg-muted"></div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <div className="flex justify-between items-center mb-6">
                    <div>
                      <div className="h-4 w-40 bg-muted rounded-full mb-2"></div>
                      <div className="h-3 w-24 bg-muted/60 rounded-full"></div>
                    </div>
                    <div className="h-8 w-20 bg-primary/20 rounded-md"></div>
                  </div>

                  <div className="grid grid-cols-3 gap-4 mb-6">
                    {[
                      { icon: <Zap className="h-4 w-4 text-primary" />, label: "Performance" },
                      { icon: <Shield className="h-4 w-4 text-primary" />, label: "Security" },
                      { icon: <Globe className="h-4 w-4 text-primary" />, label: "Global" }
                    ].map((item, idx) => (
                      <div key={idx} className="bg-background/50 rounded-lg p-4 flex flex-col items-center">
                        <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center mb-2">
                          {item.icon}
                        </div>
                        <div className="text-xs">{item.label}</div>
                        <div className="h-2 w-12 bg-muted rounded-full mt-2"></div>
                      </div>
                    ))}
                  </div>

                  <div className="space-y-3">
                    {[...Array(3)].map((_, idx) => (
                      <div key={idx} className="flex justify-between p-3 bg-muted/20 rounded-md">
                        <div className="flex items-center gap-3">
                          <div className="h-8 w-8 rounded-full bg-muted"></div>
                          <div>
                            <div className="h-2.5 w-24 bg-muted rounded-full mb-1"></div>
                            <div className="h-2 w-16 bg-muted/60 rounded-full"></div>
                          </div>
                        </div>
                        <div className="h-6 w-16 bg-muted rounded-md"></div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Floating elements */}
            <motion.div
              className="absolute -top-6 -right-2 sm:-right-8 p-3 bg-card border rounded-lg shadow-lg"
              animate={{ y: [0, -8, 0] }}
              transition={{ repeat: Infinity, duration: 3, ease: "easeInOut" }}
            >
              <div className="flex items-center gap-2">
                <div className="h-6 w-6 rounded-full bg-green-500 flex items-center justify-center text-white text-sm font-bold">✓</div>
                <div className="text-xs font-medium">Connected</div>
              </div>
            </motion.div>

            <motion.div
              className="absolute -bottom-6 -left-2 sm:-left-8 p-3 bg-card border rounded-lg shadow-lg"
              animate={{ y: [0, 8, 0] }}
              transition={{ repeat: Infinity, duration: 4, ease: "easeInOut", delay: 1 }}
            >
              <div className="flex items-center gap-2">
                <div className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center">
                  <Shield className="h-3 w-3 text-primary" />
                </div>
                <div className="text-xs font-medium">Secured</div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Hero;
